import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ComplianceClient } from '../../api/compliance-client.service';
import { ComplianceGroupResponse } from '../../api/models/compliance-group-response.model';
import { FacilityResponse } from '../../api/models/facility-response.model';
import {
    addMatchGroup,
    addMatchGroupFailure,
    addMatchGroupSuccess,
    deleteMatchGroup,
    deleteMatchGroupFailure,
    deleteMatchGroupItem,
    deleteMatchGroupItemFailure,
    deleteMatchGroupItemSuccess,
    deleteMatchGroupSuccess,
    getMatchGroupPermissions,
    getMatchGroupPermissionsFailure,
    getMatchGroupPermissionsSuccess,
    loadFacilitySearchResults,
    loadFacilitySearchResultsFailure,
    loadFacilitySearchResultsSuccess,
    loadMatchGroups,
    loadMatchGroupsFailure,
    loadMatchGroupsSuccess,
    updateMatchGroup,
    updateMatchGroupFailure,
    updateMatchGroupSuccess
} from '../actions/match-group.actions';

@Injectable()
export class MatchGroupEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly complianceClient: ComplianceClient) { }

    loadMatchGroups$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadMatchGroups),
            switchMap((action) => {
                return this.complianceClient.getComplianceGroup().pipe(
                    map((response: ComplianceGroupResponse) => loadMatchGroupsSuccess({ response: response })),
                    catchError((result) => of(loadMatchGroupsFailure({ error: result })))
                );
            })
        )
    );

    loadFacilitySearchResults$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadFacilitySearchResults),
            switchMap((action) => {
                return this.complianceClient.getFacilitiesAndSystems(action.facilityOrSystem, action.name).pipe(
                    map((response: FacilityResponse) => loadFacilitySearchResultsSuccess({ response: response })),
                    catchError((result) => of(loadFacilitySearchResultsFailure({ error: result })))
                );
            })
        )
    );

    addMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(addMatchGroup),
            switchMap((action) => {
                const data = { facilityIds: action.facilityIds, systemIds: action.systemIds };
                return this.complianceClient.createGroup(action.name, data as any).pipe(
                    map(() => addMatchGroupSuccess()),
                    catchError((result) => of(addMatchGroupFailure({ error: result })))
                );
            })
        )
    );

    reloadGroups$ = createEffect(() =>
        this._actions$.pipe(
            ofType(addMatchGroupSuccess, updateMatchGroupSuccess, deleteMatchGroupSuccess, deleteMatchGroupItemSuccess),
            map(() => loadMatchGroups())
        )
    );

    updateMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateMatchGroup),
            switchMap((action) => {
                const data = { facilityIds: action.facilityIds, systemIds: action.systemIds, isStrike: action.isStrike, groupName: action.name };
                return this.complianceClient
                    .updateGroup(action.id, data)
                    .pipe(
                        map(() => updateMatchGroupSuccess()),
                        catchError((result) => of(updateMatchGroupFailure({ error: result })))
                    );
            })
        )
    );

    deleteMatchGroup$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteMatchGroup),
            switchMap((action) => {
                return this.complianceClient.deleteGroup(action.id).pipe(
                    map(() => deleteMatchGroupSuccess()),
                    catchError((result) => of(deleteMatchGroupFailure({ error: result })))
                );
            })
        )
    );

    deleteMatchGroupItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(deleteMatchGroupItem),
            switchMap((action) => {
                return this.complianceClient.deleteGroupItem(action.facilityId, action.groupId, action.isStrike).pipe(
                    map(() => deleteMatchGroupItemSuccess()),
                    catchError((result) => of(deleteMatchGroupItemFailure({ error: result })))
                );
            })
        )
    );

    getMatchGroupPermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getMatchGroupPermissions),
            switchMap((action) => {
                return this.complianceClient.getComplianceGroupPermissions().pipe(
                    map((result) => getMatchGroupPermissionsSuccess({ permissions: result })),
                    catchError((result) => of(getMatchGroupPermissionsFailure({ error: result })))
                );
            })
        )
    );
}
